import Vue from "vue";
import vuetify from "./plugins/vuetify";
import _ from 'lodash';
import "./filters";
import App from "./App.vue";
import router from "./router";
import store, {storage, setStorage, BASE_API_URL} from "./store";
import {ValidationProvider, ValidationObserver} from 'vee-validate';
import DeviceDetector from "device-detector-js";
import {
  addValidationRules,
  addCustomRegexValidationRule
} from '@/utils/auth/validation';
import {initFirebaseApp} from '@/firebase';
import firebaseMessaging from '@/firebase/firebase-messaging';
import pwaUtilities from '@/utils/pwa';

import "./registerServiceWorker";

/* Axios */
import axios from "axios";
import VueAxios from "vue-axios";
import i18n from './i18n';
import VueClipboard from 'vue-clipboard2'
import VueMoment from 'vue-moment'
import moment from 'moment';
import 'moment/locale/de';
import log from 'loglevel'
import VueQrcode from '@chenfengyuan/vue-qrcode'
import VueCountryCode from 'vue-country-code'
import VueAwesomeSwiper from 'vue-awesome-swiper'
import 'swiper/css/swiper.css'
import {
  getSlugFromUrl,
  setAppLanguage
} from "./utils";
import {getStorage} from "@/utils/storage";

if (process.env.NODE_ENV === "development") {
  log.setLevel(log.levels.DEBUG)
}

async function getClientID(slug) {
  try {
    let response = await Vue.axios.get(`${BASE_API_URL}/api/v1/client-id/${slug}/`);
    if (!_.get(response, 'data.id')) {
      throw new Error('response empty')
    }
    return _.get(response, 'data.id');
  } catch (error) {
    log.error('Error fetching client-id: ', error)
  }
}

function configureVue() {
  Vue.config.productionTip = false;

  // TODO: check configuration (e.g. slug is set)
  Vue.use(VueMoment, {moment: moment}); // sets locale
  Vue.use(VueAxios, axios);
  Vue.use(VueAwesomeSwiper)
  Vue.use(VueCountryCode);
  Vue.use(VueClipboard)
  VueClipboard.config.autoSetContainer = true;


  Vue.component('ValidationObserver', ValidationObserver);
  Vue.component('ValidationProvider', ValidationProvider);
  Vue.component(VueQrcode.name, VueQrcode);

  Vue.axios.defaults.headers.post['Content-Type'] = 'application/json';

  Vue.axios.defaults.baseURL = BASE_API_URL
  if (BASE_API_URL) {
    log.debug("Base URL: " + BASE_API_URL)
  }

  Vue.axios.interceptors.request.use((config) => {
    let access_token = store.state.auth.access_token;
    if (access_token && !config.headers.common.hasOwnProperty('Authorization')) {
      config.headers.common.Authorization = `Token ${access_token}`;
    }
    return config;
  });
}

async function authenticateFromStorage() {
  let access_token = storage.getItem('accessToken');
  if (access_token) {
    await store.dispatch('userAuthenticate', {access_token});
  }
}

async function tryFirebaseConfig() {
  const firebaseDisabled =
    _.get(store.getters.webviewSettings, 'disable_firebase', false);

  if (firebaseDisabled) {
    return
  }

  await store.dispatch(
    'setFirebaseConfig',
    {slug: getSlugFromUrl(), baseUrl: BASE_API_URL}
  );

  initFirebaseApp(store.state.firebaseConfig);

  if (
    'Notification' in window
      && Notification.permission === 'granted'
      && parseInt(storage.getItem('fcmTokenSent'))
  ) {
    firebaseMessaging();
  }
}

function createVueInstance() {
  new Vue({
    router,
    store,
    i18n,
    vuetify,
    render: (h) => h(App)
  }).$mount("#app");
}

function setLoyalty() {
  const couponEnabled = _.get(store.getters.extensionSettings, 'loyalty.coupon.enabled', false);
  const rksvEnabled = _.get(store.getters.extensionSettings, 'loyalty.rksv.enabled', false);

  store.dispatch('setLoyaltyScan', {
    hasLoyaltyScan: couponEnabled || rksvEnabled,
    couponEnabled,
    rksvEnabled
  });
}

function getFriendInviteCode() {
  let code = new URL(location).searchParams.get('invite_code');
  if (code) {
    const expireDays = _.get(store.getters.extensionSettings, 'friend-invites.redeem_timeout', 1);
    const expireDateMS = moment().add('days', expireDays).valueOf();

    storage.setItem('inviteCode', JSON.stringify({code, exp: expireDateMS}));
    history.replaceState(null, null, process.env.BASE_URL);
  }
}

function setCustomStyles() {
  let css_variables = _.get(store.getters.extensionSettings, 'webview.custom_styles', false);
  let font = _.get(store.getters.extensionSettings, 'webview.font_path', false);
  if (!css_variables) {
    return
  }
  let head = document.getElementsByTagName('head')[0];
  let style = document.createElement('style');
  style.innerHTML = `:root{${css_variables}}`
  head.appendChild(style);

  if (font) {
    let link = '';
    link += `<link href="${font}" rel="stylesheet">`;

    let temp = document.createElement('template');
    link = link.trim();
    temp.innerHTML = link;
    head.append(temp.content);
  }
}

function setAppIcon() {
  const iconElements = document.querySelectorAll('[rel=icon]')
  const favicon = _.get(
    store.getters.extensionSettings, 'application.favicon', ''
  )
  const logo = _.get(
    store.getters.extensionSettings, 'application.logo', ''
  )
  const faviconUrl = _.isEmpty(favicon) ? logo : favicon

  if (!_.isEmpty(iconElements)) {
    iconElements.forEach((element) => {
      element.href = faviconUrl
    })
  }
}

function saveLanguageExtensionSettings() {
  const {languages, fallback_language, unknown_language} = _.get(store.getters.extensionSettings, 'i18n', {});

  storage.setItem('fallbackLanguage', fallback_language || process.env.VUE_APP_I18N_FALLBACK_LOCALE);
  storage.setItem('unknownLanguage', unknown_language || process.env.VUE_APP_I18N_LOCALE);
  storage.setItem('availableLanguages', languages);
}

function addCustomValidationRules() {
  addCustomRegexValidationRule({
    path: 'authSettings',
    name: 'password_validator'
  });
}

async function main(clientSlug) {
  const device = new DeviceDetector().parse(window.navigator.userAgent);

  document
    .querySelector('link[rel=manifest]')
    .setAttribute('href', `${BASE_API_URL}/api/v1/clients/${clientSlug}/config/manifest.json`)

  store.dispatch('setClientSlug', clientSlug);

  // Safari on iPadOS and MacOS have the same userAgent/platform values, but behave differently in certain cases (eg. geolocation only works on iPadOS)
  // this tells them apart
  const isIOS = (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) && !window.MSStream;
  if (device.os.name.toLowerCase() === 'mac' && isIOS) {
    device.os.name = 'iPadOS';
  }
  store.dispatch('setDevice', device);

  await store.dispatch('handleAutoLogoutParam');

  addValidationRules();

  await setAppLanguage()

  await store.dispatch('fetchInitialData')
  await authenticateFromStorage();

  await tryFirebaseConfig();

  saveLanguageExtensionSettings();
  addCustomValidationRules();
  createVueInstance();
  setLoyalty();
  getFriendInviteCode();
  setCustomStyles();
  setAppIcon();

  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data.redirect) {
      router.push({name: event.data.redirect});
    }
  });
}


// wait for client_id to be fetched
(async() => {
  configureVue();

  Vue.axios.defaults.headers['Helloagain-Origin'] = 'web-app';

  const clientSlug = getSlugFromUrl();
  let client_id = await getClientID(clientSlug);
  if (!client_id) {
    log.warn('Couldn\'t fetch client_id');
    return
  }
  await store.dispatch('setClientId', client_id);

  let storage = getStorage(client_id)
  setStorage(storage);

  pwaUtilities();
  main(clientSlug);

})();
